// ----------------------------------------------------------------------
// <copyright file="Izenda.ExportConfigModel.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed, toJS, observe } from 'mobx';
import { getOr, uniqueId, map, forEach, filter, isEmpty, invokeArgs, find } from 'lodash/fp';
import tracker from 'share/tracking/Izenda.Tracker';
import ExportConstants from 'logic/data/Izenda.ExportConstants';
import PrintConstants from 'logic/data/Izenda.PrintConstants';
import UiState from 'logic/Izenda.UiState';

@tracker
class MarginSettings {
  @observable type = ExportConstants.DEFAULT_VALUE;
  @observable top = ExportConstants.DEFAULT_VALUE;
  @observable bottom = ExportConstants.DEFAULT_VALUE;
  @observable left = ExportConstants.DEFAULT_VALUE;
  @observable right = ExportConstants.DEFAULT_VALUE;
  @observable header = ExportConstants.DEFAULT_VALUE;
  @observable footer = ExportConstants.DEFAULT_VALUE;

  constructor(data) {
    data && this.readData(data);
  }

  /**
   * Read data for margin setting
   * @param  {object} data
   */
  @action
  readData(data) {
    this.type = data.type;
    this.top = data.topValue;
    this.bottom = data.bottomValue;
    this.left = data.leftValue;
    this.right = data.rightValue;
    this.header = data.headerValue;
    this.footer = data.footerValue;
  }

  /**
   * Get data for savings
   * @return  {object} data
   */
  prepareData() {
    return {
      type: this.type,
      topValue: this.top,
      bottomValue: this.bottom,
      leftValue: this.left,
      rightValue: this.right,
      headerValue: this.header,
      footerValue: this.footer
    };
  }

  /**
   * Get data for savings
   * @param  {float} value
   * @param  {string} key
   */
  @action
  updateValueByKey(value, key) {
    this[key] = value;
    this.markDirty();
  }
}

@tracker
export default class ExportConfigModel {
  @observable id = uniqueId();
  @observable orientation = ExportConstants.DEFAULT_VALUE;
  @observable centerOnPage = {
    horizontally: false,
    vertically: false
  };
  @observable pageBreakAfterReportPart = false;

  @observable pageSizeKey = PrintConstants.DEFAULT_PAGE_SIZE_KEY;
  @observable scaleX = 1;
  @observable scaleY = 1;

  // Current preview window size
  scaleWidth = 0;
  scaleHeight = 0;

  @observable margins = ExportConstants.DEFAULT_VALUE;
  @observable marginSettings = null;

  constructor(data) {
    this.marginSettings = this.initMarginSettings(ExportConstants.DEFAULT_SETTINGS);
    data && this.readFromReport(data);
    this.resetDirty();
  }

  @computed
  get allMarginSettings() {
    return map(item => item.prepareData(), this.marginSettings);
  }

  @computed
  get currentMarginSetting() {
    const result = find({ type: this.margins }, this.marginSettings);
    return result || {};
  };

  @computed
  get customMarginSettings() {
    return find({ type: ExportConstants.MARGIN_VALUE.CUSTOM }, this.marginSettings);
  }

  @computed
  get marginSettingInInches() {
    const margin = find({ type: this.margins }, this.marginSettings);
    if (!margin)
      return {
        margin: 0
      };

    const rawMargin = toJS(margin);
    const unit = 'in';
    return {
      marginTop: rawMargin.top && rawMargin.top + unit,
      marginRight: rawMargin.right && rawMargin.right + unit,
      marginBottom: rawMargin.bottom && rawMargin.bottom + unit,
      marginLeft: rawMargin.left && rawMargin.left + unit,
      header: rawMargin.header && rawMargin.header + unit,
      footer: rawMargin.footer && rawMargin.footer + unit
    }
  }

  /**
   * Read data for export config
   * @param  {object} reportDefintions
   */
  @action
  readFromReport(reportDefintions: any) {
    const exportConfig = reportDefintions.exportFormatSetting || {};
    const centerOnPage = exportConfig.centerOnPage || {};
    this.orientation = exportConfig.orientation;
    this.margins = exportConfig.margins;
    this.centerOnPage.horizontally = centerOnPage.horizontally;
    this.centerOnPage.vertically = centerOnPage.vertically;
    this.pageBreakAfterReportPart = exportConfig.pageBreakAfterReportPart;
    this.updateMarginSettings(exportConfig.marginSettings);
  }

  /**
   * Init margin config
   * @param  {array} data
   */
  @action
  initMarginSettings(data: any[]) {
    return map(item => new MarginSettings(item), data);
  }

  /**
   * Update margin config
   * @param  {array} data
   */
  @action
  updateMarginSettings(data: any[]) {
    forEach(item => {
      item && invokeArgs('readData', [item], find({ type: item.type }, this.marginSettings))
    }, data);
  }

  /**
   * Get export config for saving
   * @return  {object} data
   */
  getExportConfig() {
    return {
      orientation: this.orientation,
      margins: this.margins,
      centerOnPage: this.centerOnPage,
      pageBreakAfterReportPart: this.pageBreakAfterReportPart,
      marginSettings: this.allMarginSettings
    };
  }

  /**
   * Update export settings by key
   * @param  {string} value
   * @param  {string} key
   */
  @action
  updateValueByKey(value: string, key: string) {
    this[key] = value;
    this.markDirty();
  }

  /**
   * Update center on page settings by key
   * @param  {bool} value
   * @param  {string} key
   */
  @action
  updateCenterOnPageByKey(value: boolean, key: string) {
    this.centerOnPage[key] = value;
    this.markDirty();
  }

  /**
   * Check model dirty
   * @return {bool} result
   */
  checkDirty() {
    const dirtyItems = filter(item => item.checkDirty && item.checkDirty(), this.marginSettings);
    return !isEmpty(dirtyItems) || this.isDirty;
  }

  /**
   * clean model dirty
   */
  @action
  cleanDirty() {
    this.resetDirty();
    forEach(marginSetting => marginSetting.resetDirty(), this.marginSettings);
  }

  /**
   * Get max value by key
   * @param {string} key
   * @returns {int} max value
   */
  getMaxByKey(key: string): number {
    const pageSize = PrintConstants.getPageSize(this.pageSizeKey, this.orientation);
    const marginSetings = this.currentMarginSetting;
    const rightValue = getOr(0, 'right', marginSetings);
    const leftValue = getOr(0, 'left', marginSetings);
    const bottomValue = getOr(0, 'bottom', marginSetings);
    const topValue = getOr(0, 'top', marginSetings);
    const headerValue = getOr(0, 'header', marginSetings);
    const footerValue = getOr(0, 'footer', marginSetings);

    let result = ExportConstants.MAX_HEIGHT;
    switch (key) {
      case ExportConstants.MARGIN_KEY.LEFT:
        result = pageSize.widthInInches - rightValue - ExportConstants.MIN_WIDTH;
        break;
      case ExportConstants.MARGIN_KEY.RIGHT:
        result = pageSize.widthInInches - leftValue - ExportConstants.MIN_WIDTH;
        break;
      case ExportConstants.MARGIN_KEY.TOP:
        result = pageSize.heightInInches - ExportConstants.MIN_HEIGHT - (bottomValue + headerValue + footerValue);
        break;
      case ExportConstants.MARGIN_KEY.BOTTOM:
        result = pageSize.heightInInches - ExportConstants.MIN_HEIGHT - (topValue + headerValue + footerValue);
        break;
      case ExportConstants.MARGIN_KEY.HEADER:
        result = pageSize.heightInInches - ExportConstants.MIN_HEIGHT - (bottomValue + topValue + footerValue);
        break;
      case ExportConstants.MARGIN_KEY.FOOTER:
        result = pageSize.heightInInches - ExportConstants.MIN_HEIGHT - (bottomValue + headerValue + topValue);
        break;
    }
    return Math.round(result * 100) / 100;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ExportConfigModel.js